import { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import BuyBookButton from './BuyBookButton'
import BookReviewForm from './BookReviewForm'
import Toast from './Toast'
import Modal from './Modal'
import { formatDate, formatDateForInput } from '../utils/dateFormat'

interface Book {
  id: number
  title: string
  author: string
  isbn?: string
  asin?: string
  bookshop_slug?: string
  description?: string
  cover_url?: string
  genre?: string
  first_publish_year?: number
  number_of_pages?: number
}

interface Review {
  id: number
  user: {
    id: number
    name: string
    email: string
  }
  rating: number
  review_text?: string
  created_at: string
}

interface Member {
  id: number
  user_id: number
  name: string
  email: string
}

interface BookPick {
  id: number
  status: string
  started_at?: string
  completed_at?: string
  book: Book
  picked_by?: {
    id: number
    name: string
    email: string
  }
  season?: {
    id: number
    season_number: number
  }
  average_rating?: number
  review_count: number
}

interface PastBookDetailProps {
  bookPick: BookPick
  clubId?: number
  onClose: () => void
  onDelete?: () => void
  userProvider?: string | null
  onProviderSelected?: (provider: string) => void
}

export default function PastBookDetail({ bookPick, clubId, onClose, onDelete, userProvider, onProviderSelected }: PastBookDetailProps) {
  const [reviews, setReviews] = useState<Review[]>([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [members, setMembers] = useState<Member[]>([])
  const [selectedPickerId, setSelectedPickerId] = useState<number | null>(bookPick.picked_by?.id || null)
  const [selectedStatus, setSelectedStatus] = useState<string>(bookPick.status)
  const [updating, setUpdating] = useState(false)
  const [showReviewForm, setShowReviewForm] = useState(false)
  const [editedStartedAt, setEditedStartedAt] = useState<string>(
    formatDateForInput(bookPick.started_at)
  )
  const [editedCompletedAt, setEditedCompletedAt] = useState<string>(
    formatDateForInput(bookPick.completed_at)
  )
  const [editedTitle, setEditedTitle] = useState<string>(bookPick.book.title)
  const [editedAuthor, setEditedAuthor] = useState<string>(bookPick.book.author)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  useEffect(() => {
    fetchReviews()
    if (clubId) {
      fetchMembers()
    }
  }, [bookPick.id, clubId])

  const fetchReviews = async () => {
    // Only fetch reviews if there's a club context
    if (!clubId) {
      setLoading(false)
      return
    }

    try {
      const response = await fetch(`/api/v1/book_picks/${bookPick.id}/reviews`)
      if (response.ok) {
        const data = await response.json()
        setReviews(data.reviews || [])
      }
    } catch (err) {
      console.error('Failed to fetch reviews:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchMembers = async () => {
    if (!clubId) return
    try {
      const response = await fetch(`/api/v1/clubs/${clubId}`)
      if (response.ok) {
        const data = await response.json()
        setMembers(data.members || [])
      }
    } catch (err) {
      console.error('Failed to fetch members:', err)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      // Use different endpoints for club vs personal books
      const url = clubId
        ? `/api/v1/clubs/${clubId}/book_picks/${bookPick.id}`
        : `/api/v1/personal_readings/${bookPick.id}`

      const response = await fetchWithCsrf(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        onDelete?.()
        onClose()
      } else {
        const error = await response.json()
        setToast({ message: `Failed to delete book: ${error.error || 'Unknown error'}`, type: 'error' })
      }
    } catch (err) {
      console.error('Failed to delete book:', err)
      setToast({ message: 'Failed to delete book. Please try again.', type: 'error' })
    } finally {
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const handleSaveEdit = async () => {
    setUpdating(true)
    try {
      // Determine the endpoint based on whether this is a club or personal book
      const url = clubId
        ? `/api/v1/clubs/${clubId}/book_picks/${bookPick.id}`
        : `/api/v1/personal_readings/${bookPick.id}`

      // Build request body
      const body: any = {
        book_pick: {
          started_at: editedStartedAt || null,
          completed_at: editedCompletedAt || null
        },
        book: {
          title: editedTitle,
          author: editedAuthor
        }
      }

      // Add club-specific fields
      if (clubId) {
        body.book_pick.picked_by_id = selectedPickerId
        body.book_pick.status = selectedStatus
      }

      const response = await fetchWithCsrf(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body)
      })

      if (response.ok) {
        setIsEditing(false)
        setToast({ message: 'Book updated successfully!', type: 'success' })
        onDelete?.() // Trigger refresh in parent
        // Close modal after a brief delay to show the toast
        setTimeout(() => {
          onClose()
        }, 1500)
      } else {
        const error = await response.json()
        setToast({ message: `Failed to update book: ${error.error || 'Unknown error'}`, type: 'error' })
      }
    } catch (err) {
      console.error('Failed to update book:', err)
      setToast({ message: 'Failed to update book. Please try again.', type: 'error' })
    } finally {
      setUpdating(false)
    }
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setSelectedPickerId(bookPick.picked_by?.id || null)
    setSelectedStatus(bookPick.status)
    setEditedStartedAt(formatDateForInput(bookPick.started_at))
    setEditedCompletedAt(formatDateForInput(bookPick.completed_at))
    setEditedTitle(bookPick.book.title)
    setEditedAuthor(bookPick.book.author)
  }

  const renderStars = (rating: number) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    )
  }

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        size="xl"
        showCloseButton={true}
        maxHeight="90vh"
        className="flex flex-col my-8 overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Book Details</h2>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Book Overview Section */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Cover */}
              {bookPick.book.cover_url ? (
                <img
                  src={bookPick.book.cover_url}
                  alt={bookPick.book.title}
                  className="w-32 h-48 object-cover rounded-lg shadow-md flex-shrink-0"
                />
              ) : (
                <div className="w-32 h-48 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg shadow-md flex items-center justify-center flex-shrink-0">
                  <svg className="w-12 h-12 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
              )}

              {/* Book Info */}
              <div className="flex-1">
                {isEditing ? (
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Title
                      </label>
                      <input
                        type="text"
                        value={editedTitle}
                        onChange={(e) => setEditedTitle(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg font-bold"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
                        Author
                      </label>
                      <input
                        type="text"
                        value={editedAuthor}
                        onChange={(e) => setEditedAuthor(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{bookPick.book.title}</h3>
                    <p className="text-lg text-gray-600 mb-4">by {bookPick.book.author}</p>
                  </>
                )}

                {/* Meta Info */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {bookPick.book.genre && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Genre</p>
                      <p className="text-sm font-medium text-gray-900">{bookPick.book.genre}</p>
                    </div>
                  )}
                  {bookPick.book.first_publish_year && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Published</p>
                      <p className="text-sm font-medium text-gray-900">{bookPick.book.first_publish_year}</p>
                    </div>
                  )}
                  {bookPick.book.number_of_pages && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Pages</p>
                      <p className="text-sm font-medium text-gray-900">{bookPick.book.number_of_pages}</p>
                    </div>
                  )}
                  {bookPick.season && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Season</p>
                      <p className="text-sm font-medium text-gray-900">Season {bookPick.season.season_number}</p>
                    </div>
                  )}
                </div>

                {/* Club Info */}
                <div className="flex flex-wrap gap-4 text-sm">
                  {isEditing && clubId ? (
                    <div className="w-full space-y-4">
                      <div>
                        <label className="block text-xs text-gray-500 uppercase tracking-wide mb-2">
                          Picked By
                        </label>
                        <select
                          value={selectedPickerId || ''}
                          onChange={(e) => setSelectedPickerId(e.target.value ? parseInt(e.target.value) : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="">Unclaimed</option>
                          {members.map(member => (
                            <option key={member.user_id} value={member.user_id}>
                              {member.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 uppercase tracking-wide mb-2">
                          Status
                        </label>
                        <select
                          value={selectedStatus}
                          onChange={(e) => setSelectedStatus(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="upcoming">Upcoming</option>
                          <option value="current">Currently Reading</option>
                          <option value="completed">Completed</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <>
                      {bookPick.picked_by ? (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-gray-600">Picked by <span className="font-medium text-gray-900">{bookPick.picked_by.name}</span></span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-gray-400 italic">(Unclaimed)</span>
                        </div>
                      )}
                    </>
                  )}
                  {isEditing ? (
                    <div className="w-full grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-500 uppercase tracking-wide mb-2">
                          Started Date
                        </label>
                        <input
                          type="date"
                          value={editedStartedAt}
                          onChange={(e) => setEditedStartedAt(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 uppercase tracking-wide mb-2">
                          Completed Date
                        </label>
                        <input
                          type="date"
                          value={editedCompletedAt}
                          onChange={(e) => setEditedCompletedAt(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      {bookPick.started_at && (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-600">Started <span className="font-medium text-gray-900">{formatDate(bookPick.started_at)}</span></span>
                        </div>
                      )}
                      {bookPick.completed_at && (
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-600">Completed <span className="font-medium text-gray-900">{formatDate(bookPick.completed_at)}</span></span>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Average Rating */}
                {bookPick.average_rating && (
                  <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {renderStars(Math.round(bookPick.average_rating))}
                          <span className="text-lg font-bold text-gray-900">{bookPick.average_rating}</span>
                        </div>
                        <p className="text-xs text-gray-600">{bookPick.review_count} {bookPick.review_count === 1 ? 'review' : 'reviews'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            {bookPick.book.description && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">About this book</h4>
                <p className="text-sm text-gray-700 leading-relaxed">{bookPick.book.description}</p>
              </div>
            )}
          </div>

          {/* Reviews Section - Only show for club books */}
          {clubId && (
            <div className="p-6">
              <h4 className="text-lg font-bold text-gray-900 mb-4">Member Reviews</h4>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : reviews.length > 0 ? (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-gray-900">{review.user.name}</p>
                          <p className="text-xs text-gray-500">{formatDate(review.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {renderStars(review.rating)}
                          <span className="text-sm font-medium text-gray-900">{review.rating}</span>
                        </div>
                      </div>
                      {review.review_text && (
                        <p className="text-sm text-gray-700 leading-relaxed">{review.review_text}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  <p className="text-gray-600 text-sm">No reviews yet</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          {showDeleteConfirm ? (
            <div className="space-y-3">
              <p className="text-sm text-gray-700 text-center">
                Are you sure you want to delete "{bookPick.book.title}"? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 px-6 py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all disabled:opacity-50"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          ) : isEditing ? (
            <div className="flex gap-3">
              <button
                onClick={handleCancelEdit}
                disabled={updating}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 font-semibold rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={updating}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30 disabled:opacity-50"
              >
                {updating ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-3">
              {/* Buy Button */}
              <BuyBookButton
                book={bookPick.book}
                userProvider={userProvider}
                onProviderSelected={onProviderSelected}
                variant="primary"
                size="lg"
              />

              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-6 py-3 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-all"
              >
                Delete
              </button>

              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-3 bg-white border-2 border-indigo-300 text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition-all"
              >
                Edit
              </button>

              {clubId && (
                <button
                  onClick={() => setShowReviewForm(true)}
                  className="px-6 py-3 bg-white border-2 border-green-300 text-green-700 font-semibold rounded-lg hover:bg-green-50 transition-all"
                >
                  Review
                </button>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Book Review Form Modal */}
      {showReviewForm && clubId && (
        <BookReviewForm
          bookPickId={bookPick.id}
          onClose={() => setShowReviewForm(false)}
          onSuccess={() => {
            setShowReviewForm(false)
            fetchReviews()
          }}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  )
}
